import { OrderStatusDefinitionIdentifier } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { isPickupOrder } from '@/entities/order/lib/isPickupOrder';
import { OrderNumber } from '@/entities/order/ui/order-number';
import { OrderProgress, orderStateMap } from '@/entities/order/ui/order-progress';
import { getPaymentRedirectUrl } from '@/features/order/api/get-payment-redirect-url';
import { QueryApi } from '@/features/query/api';
import messages from '@/shared/i18n/locales/en-GB.json';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import './order-progress-alert.scss';

const reasonKeys = messages.ORDER_PROGRESS_ALERT.CANCELED.REASONS;
type ReasonKey = keyof typeof reasonKeys;

type Props = {
    orderId: string;
    onClose?: () => void;
};
export const OrderProgressAlert = ({ orderId, onClose }: Props) => {
    const t = useTranslations();
    const locale = useLocale();
    const isReasonKey = (key: string): key is ReasonKey =>
        Object.prototype.hasOwnProperty.call(reasonKeys, key);
    const orderQuery = useQuery({
        ...QueryApi.Order.queries.Order(orderId),
    });
    const deliveryMethodsQuery = useQuery(QueryApi.DeliveryMethods.queries.DeliveryMethods);

    const orderState = `${orderQuery.data?.status?.definition.identifier}` as OrderStatusDefinitionIdentifier;
    const state = orderStateMap[orderState] ?? 'created';
    const isPickup = isPickupOrder(orderQuery.data ?? {}, deliveryMethodsQuery.data);

    const stateKey = state.toUpperCase() as Uppercase<NonNullable<typeof state>>;

    const paymentRedirectQuery = useQuery({
        queryKey: ['order-payment-redirect', orderId],
        queryFn: () => getPaymentRedirectUrl(orderId),
        enabled: Boolean(orderId) && orderState === OrderStatusDefinitionIdentifier.AWAITING_PAYMENT,
        staleTime: 60_000,
    });

    const expectedDeliveryTime = orderQuery.data?.expectedToBePreparedAt;
    const stateDescription = useMemo(() => {
        switch (state) {
            case 'awaitingPayment':
                return t('ORDER_PROGRESS_ALERT.AWAITINGPAYMENT.DESCRIPTION');
            case 'created':
                if (orderQuery.data?.scheduledAt) {
                    return t('ORDER_PROGRESS_ALERT.CREATED.TIMED_REASSURANCE', {
                        time: dayjs(orderQuery.data.scheduledAt).format('HH:mm'),
                    });
                }
                return t(`ORDER_PROGRESS_ALERT.CREATED.DESCRIPTION`);
            case 'review':
                return t(`ORDER_PROGRESS_ALERT.REVIEW.DESCRIPTION`);
            case 'acceptedTimed':
                const scheduledTime = orderQuery.data?.scheduledAt;
                if (!scheduledTime && !expectedDeliveryTime) {
                    return '';
                }
                return t(
                    `ORDER_PROGRESS_ALERT.ACCEPTEDTIMED.DESCRIPTION${expectedDeliveryTime ? '_DELIVERY' : ''}`,
                    {
                        date: dayjs(expectedDeliveryTime || scheduledTime).format('DD.MM.'),
                        time: dayjs(expectedDeliveryTime || scheduledTime).format('HH:mm'),
                    },
                );
            case 'accepted':
            case 'cooking':
                return expectedDeliveryTime
                    ? t(
                          isPickup
                              ? 'ORDER_PROGRESS_ALERT.EXPECTED_PICKUP'
                              : 'ORDER_PROGRESS_ALERT.EXPECTED_DELIVERY',
                          { time: dayjs(expectedDeliveryTime).format('HH:mm') },
                      )
                    : '';
            case 'cooked':
                return expectedDeliveryTime
                    ? t(
                          isPickup
                              ? 'ORDER_PROGRESS_ALERT.COOKED.DESCRIPTION_PICKUP'
                              : 'ORDER_PROGRESS_ALERT.COOKED.DESCRIPTION',
                          { time: dayjs(expectedDeliveryTime).format('HH:mm') },
                      )
                    : null;
            case 'delivering': {
                const minutesLeft = expectedDeliveryTime
                    ? dayjs(expectedDeliveryTime).diff(dayjs(), 'minute')
                    : null;
                if (minutesLeft === null || minutesLeft <= 0) {
                    return t('ORDER_PROGRESS_ALERT.DELIVERING.DESCRIPTION_LATE');
                }
                return t(`ORDER_PROGRESS_ALERT.DELIVERING.DESCRIPTION`, {
                    time: minutesLeft,
                });
            }
            case 'canceled':
                const reasonsList = orderQuery.data?.ruleSet?.cancel.reasons
                    ?.map(({ titleKey }) => titleKey)
                    .filter((titleKey): titleKey is ReasonKey => !!titleKey && isReasonKey(titleKey))
                    .map((titleKey) => t(`ORDER_PROGRESS_ALERT.CANCELED.REASONS.${titleKey}`));
                const reasons = reasonsList?.length
                    ? new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(reasonsList)
                    : null;
                return reasons
                    ? t(`ORDER_PROGRESS_ALERT.CANCELED.DESCRIPTION_WITH_REASON`, {
                          reason: reasons,
                      })
                    : t(`ORDER_PROGRESS_ALERT.CANCELED.DESCRIPTION`);
            default:
                return '';
        }
    }, [
        stateKey,
        t,
        state,
        isPickup,
        expectedDeliveryTime,
        orderQuery.data?.scheduledAt,
        orderQuery.data?.ruleSet?.cancel.reasons,
        locale,
    ]);

    /*const handleShowLogin = () => {
        ModalService.show(LoginModal, {});
    };*/

    if (!orderQuery.data) {
        return null;
    }

    return (
        <div
            className="order-progress-alert"
            data-testid="order-progress-alert"
            data-order-state={orderState}
            data-state={state}
        >
            <div className="order-progress-alert__header">
                <OrderNumber
                    orderNumber={orderQuery.data.scopedNumber}
                    className="order-progress-alert__order-number"
                    data-testid="order-progress-alert-order-number"
                    data-value={orderQuery.data.number}
                />
                <button
                    className="order-progress-alert__close"
                    onClick={onClose}
                    type="button"
                    aria-label="Close"
                    data-testid="order-progress-alert-close-button"
                >
                    <Icon name="close" data-testid="order-progress-alert-close-icon" />
                </button>
            </div>
            <div className="order-progress-alert__content">
                <h2 data-testid="order-progress-alert-title">
                    {t(`ORDER_PROGRESS_ALERT.${stateKey}.TITLE`)}
                </h2>
                <p data-testid="order-progress-alert-description">{stateDescription}</p>
            </div>
            <div className="order-progress-alert__progress">
                <OrderProgress state={state} size="big" data-testid="order-progress-alert-progress" />
            </div>
            {paymentRedirectQuery.data ? (
                <Button
                    asChild
                    variant="secondary"
                    className="order-progress-alert__button"
                    data-testid="order-progress-alert-check-status"
                >
                    <a href={paymentRedirectQuery.data}>{t('ORDER_PROGRESS_ALERT.AWAITINGPAYMENT.BUTTON')}</a>
                </Button>
            ) : null}
            {/*points && (
                <>
                    <p data-testid="order-progress-alert-points-message">
                        {t('ORDER_PROGRESS_ALERT.POINTS_MESSAGE', { points })}
                    </p>
                    <Button
                        className="order-progress-alert__button"
                        onClick={handleShowLogin}
                        data-testid="order-progress-alert-login-button"
                    >
                        {t('ORDER_PROGRESS_ALERT.LOGIN_BUTTON')}
                    </Button>
                </>
            )*/}
        </div>
    );
};
